import { useCallback, useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { Button, Col, Row } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import Title from "antd/es/typography/Title";

import { queryKeys } from "hooks/queryKeys";
import { useGetProperties } from "hooks/useGetProperties";
import usePageLoading from "hooks/usePageLoading";

import {
  PageLoadingContainer,
  SkeletonBlock,
  SkeletonHeading,
  SkeletonInput,
  SlowStateBanner,
  TableSkeleton,
} from "js/components/Loading";
import CreatePropertyModal from "js/components/Properties/ModernPropertyModal/CreatePropertyModal";
import PropertiesTable from "js/components/Properties/PropertiesTable/PropertiesTable";
import TableSearch from "js/components/TableSearch";
import { PaginationTable, Property } from "js/components/types";

/**
 * Referencia estable para el caso "sin datos". NO MUTAR: se comparte entre
 * renders. Ver la nota del efecto de `propertyData` mas abajo.
 */
const EMPTY_PROPERTIES: Property[] = [];

const PropertiesDashboard = () => {
  const screen = useBreakpoint();
  const [filteredData, setFilteredData] = useState<Property[]>([]);
  const [propertyId, setPropertyId] = useState("");
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationTable>({
    current: 1,
    pageSize: 15,
    total: 0,
  });

  const {
    data,
    isLoading,
    refetch: refetchProperties,
  } = useGetProperties({
    pagination,
  });

  /**
   * Antes esto era el parametro por defecto `data: propertyData = []`, que
   * produce un array NUEVO en cada render mientras la query no tiene datos.
   * El efecto de abajo depende de `[propertyData]`, asi que corria en CADA
   * render y siempre escribia estado -> bucle infinito INDEPENDIENTE de
   * TableSearch ("Maximum update depth exceeded").
   * Con una constante a nivel de modulo la referencia es estable y el efecto
   * corre solo cuando los datos cambian de verdad.
   */
  const propertyData = data ?? EMPTY_PROPERTIES;

  const { showSkeleton, showSlowState, dismissSlowState } = usePageLoading({
    isLoading,
  });

  const handleRetryFetch = () => {
    dismissSlowState();
    void refetchProperties();
  };

  const slowStateBanner = showSlowState ? (
    <SlowStateBanner
      actions={
        <Button
          size="small"
          style={{
            borderColor: "#fcd34d",
            color: "#92400e",
            backgroundColor: "#fff7ed",
          }}
          onClick={handleRetryFetch}
        >
          Reintentar
        </Button>
      }
    >
      <p className="font-medium">Esto está tardando más de lo previsto.</p>
      <p>Verifica tu conexión o vuelve a intentarlo en unos segundos.</p>
    </SlowStateBanner>
  ) : null;

  const headerSkeleton = (
    <div className="space-y-4">
      <SkeletonHeading className="h-9 w-56" />
    </div>
  );

  const actionsSkeleton = (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between lg:gap-0">
      <SkeletonInput className="lg:w-[30%]" />
      <SkeletonBlock className="h-10 w-full rounded-md lg:w-[20%]" />
    </div>
  );

  const contentSkeleton = (
    <div className="space-y-4">
      <TableSkeleton columns={6} rows={6} />
    </div>
  );

  const queryClient = useQueryClient();

  // Create a setter function to maintain compatibility with existing components
  const setPropertyData = (newData: Property[]) => {
    const queryKey = queryKeys.properties.list({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });

    queryClient.setQueryData<Property[]>(queryKey, newData);
    setFilteredData(newData);
    setPagination((prev) => ({
      ...prev,
      total: newData.length,
    }));
  };

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (!searchValue) {
        setFilteredData(propertyData);
        return;
      }

      if (!propertyData || !Array.isArray(propertyData)) {
        return;
      }

      const lowercasedValue = searchValue.toLowerCase();
      const filtered = propertyData.filter((property) => {
        const { alias, address } = property;
        return (
          alias?.toLowerCase().includes(lowercasedValue) ||
          address?.street?.toLowerCase().includes(lowercasedValue) ||
          address?.neighborhood?.toLowerCase().includes(lowercasedValue) ||
          address?.city?.toLowerCase().includes(lowercasedValue)
        );
      });

      setFilteredData(filtered);
    },
    [propertyData],
  );

  // Memoizado: es el `onClear` que pasaba a TableSearch con identidad nueva
  // en cada render.
  const handleClearSearch = useCallback(() => {
    setFilteredData(propertyData);
  }, [propertyData]);

  // Update filtered data when propertyData changes
  useEffect(() => {
    setFilteredData(propertyData);
    // Idempotente: `{ ...prev }` siempre crea objeto nuevo, y `pagination`
    // viaja dentro del queryKey -> escribirlo sin necesidad dispara un
    // refetch de red extra.
    setPagination((prev) =>
      prev.total === propertyData.length
        ? prev
        : { ...prev, total: propertyData.length },
    );
  }, [propertyData]);

  return (
    <>
      <PageLoadingContainer
        showSkeleton={showSkeleton}
        slowState={slowStateBanner}
        headerSkeleton={headerSkeleton}
        actionsSkeleton={actionsSkeleton}
        contentSkeleton={contentSkeleton}
        className="mb-6"
      >
        <div>
          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            justify="start"
            align="middle"
          >
            <Col xs={24} sm={24} md={12} lg={6}>
              <Title level={1} style={{ marginBottom: 5 }}>
                Inmuebles
              </Title>
            </Col>
          </Row>
          <Row
            gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
            align="middle"
            style={{ marginBottom: 15, marginTop: 10 }}
          >
            <Col
              xs={24}
              sm={24}
              md={24}
              lg={24}
              style={{
                display: "flex",
                flexDirection: !screen.lg ? "column" : "row",
                justifyContent: "space-between",
                gap: !screen.lg ? 10 : 0,
              }}
            >
              <div style={{ width: !screen.lg ? "100%" : "30%" }}>
                <TableSearch
                  reapplyOn={propertyData}
                  screen={screen}
                  onSearch={handleSearch}
                  onClear={handleClearSearch}
                  label="inmueble(s)"
                />
              </div>
              <Button
                type="primary"
                block
                onClick={() => {
                  setPropertyId("");
                  setIsPropertyModalOpen(true);
                }}
                style={{ width: !screen.lg ? "100%" : "20%" }}
              >
                Registrar Inmueble
              </Button>
            </Col>
          </Row>
          <div>
            <PropertiesTable
              data={propertyData}
              loading={isLoading}
              pagination={pagination}
              setPagination={setPagination}
              setIsPropertyModalOpen={setIsPropertyModalOpen}
              setPropertyId={setPropertyId}
              screen={screen}
              filteredData={filteredData}
              setFilteredData={setFilteredData}
              fromOrganization={false}
            />
          </div>
        </div>
      </PageLoadingContainer>
      <CreatePropertyModal
        isOpen={isPropertyModalOpen}
        setIsOpen={setIsPropertyModalOpen}
        propertyId={propertyId}
        setPropertyId={setPropertyId}
        propertyData={propertyData}
        setPropertyData={setPropertyData}
      />
    </>
  );
};

export default PropertiesDashboard;
